import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Wrench, RefreshCw, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import ConfirmDialog from "../components/ConfirmDialog";

/**
 * NotProceedingMigrationSection — admin-only maintenance action that
 * finds every project with legacy `status="cancelled"` that isn't yet
 * parked in `lifecycle_status="not_proceeding"`, and bulk-moves them
 * there so investors see them in the correct Kanban column and their
 * kWp is excluded from installed/pipeline aggregates.
 *
 * Two-step flow — Preview first, then Apply. Every applied move
 * records a lifecycle_history entry ("Bulk migrated from legacy
 * status=cancelled") and is audited.
 */
export default function NotProceedingMigrationSection() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/sanity/not-proceeding-migration");
      setPreview(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const apply = async () => {
    if (!preview || preview.count === 0) return;
    setConfirmOpen(true);
  };

  const doApply = async () => {
    setConfirmOpen(false);
    setApplying(true);
    try {
      const r = await api.post("/settings/sanity/not-proceeding-migration/apply");
      setLastResult(r.data);
      toast.success(`Migrated ${r.data.fixed} project${r.data.fixed === 1 ? "" : "s"} to Not Proceeding`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Migration failed");
    } finally {
      setApplying(false);
    }
  };

  return (
    <section className="swiss-card-flat p-6 mb-6" data-testid="np-migration-section">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Wrench size={14} />
            <div className="label">Bulk Not-Proceeding migration</div>
          </div>
          <p className="muted text-xs mt-1 max-w-2xl">
            Legacy <code className="font-mono">status="cancelled"</code> projects predate the
            new <em>Not Proceeding</em> Kanban column. Bulk-migrate them so investors see them
            where expected — and their kWp is properly excluded from installed / pipeline totals.
          </p>
        </div>
        <button
          className="btn-ghost inline-flex items-center gap-1.5"
          onClick={refresh}
          disabled={loading}
          data-testid="np-migration-refresh"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {preview && preview.count === 0 && (
        <div className="text-sm text-[#17663C] inline-flex items-center gap-2 mb-2"
             data-testid="np-migration-clean">
          <CheckCircle2 size={14} /> No candidates — every cancelled project is already parked in Not Proceeding.
        </div>
      )}

      {preview && preview.count > 0 && (
        <>
          <div className="text-sm inline-flex items-center gap-2 mb-3"
               style={{ color: "#8A5410" }}
               data-testid="np-migration-count">
            <AlertTriangle size={14} />
            {preview.count} legacy cancelled project{preview.count === 1 ? "" : "s"} to migrate
          </div>
          <div className="border border-[var(--line)] rounded overflow-hidden mb-4 max-h-[280px] overflow-y-auto">
            <table className="data-table" data-testid="np-migration-table">
              <thead>
                <tr>
                  <th className="text-left">Project</th>
                  <th className="text-left">Customer</th>
                  <th className="text-left">Current lifecycle</th>
                  <th className="text-left">Class</th>
                </tr>
              </thead>
              <tbody>
                {preview.projects.map((p) => (
                  <tr key={p.id} data-testid={`np-migration-row-${p.id}`}>
                    <td className="font-medium">{p.name}</td>
                    <td className="muted">{p.customer_name || "—"}</td>
                    <td className="font-mono text-xs muted">{p.lifecycle_status || "—"}</td>
                    <td className="text-xs muted">{p.business_classification || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            className="btn-primary inline-flex items-center gap-1.5"
            onClick={apply}
            disabled={applying}
            data-testid="np-migration-apply"
          >
            <Wrench size={12} />
            {applying ? "Migrating…" : `Migrate ${preview.count} project${preview.count === 1 ? "" : "s"}`}
          </button>
        </>
      )}

      {lastResult && lastResult.fixed > 0 && (
        <div className="mt-4 text-xs muted" data-testid="np-migration-result">
          Last run: migrated {lastResult.fixed} project{lastResult.fixed === 1 ? "" : "s"} to Not Proceeding.
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Bulk migrate cancelled projects?"
        description={
          preview && preview.count > 0
            ? `Move ${preview.count} cancelled project${preview.count === 1 ? "" : "s"} into the Not Proceeding Kanban column. Each move is audited and adds a history entry with reason "Bulk migrated from legacy status=cancelled".`
            : "No projects to migrate."
        }
        confirmLabel="Migrate now"
        confirmVariant="primary"
        icon="wrench"
        onConfirm={doApply}
        onCancel={() => setConfirmOpen(false)}
      />
    </section>
  );
}
